/** Types van de productcatalogus; spiegelt packages/backend/src/types/product.ts. */

export type PriceMode = 'hours' | 'fixed' | 'on_request';

export type BillingPeriod = 'one_time' | 'four_weekly' | 'monthly' | 'quarterly' | 'yearly';

export const BILLING_PERIOD_LABELS: Record<BillingPeriod, string> = {
  one_time: 'eenmalig',
  four_weekly: 'per 4 weken',
  monthly: 'per maand',
  quarterly: 'per kwartaal',
  yearly: 'per jaar',
};

export interface ProductVariant {
  id: string;
  name: string;
  note?: string;
  /** Richtext voor de omschrijvingscel wanneer het product één offerteregel vormt. */
  descriptionHtml?: string;
}

export interface ProductLine {
  id: string;
  code: string;
  description: string;
  rateKey: string;
  hours: Record<string, number | string>;
  hidden?: boolean;
}

export interface ProductCost {
  mode: 'none' | 'amount' | 'markup';
  amountCentsByVariant?: Record<string, number>;
  markupPct?: number;
}

export interface BundleComponent {
  id: string;
  productId: string;
  productVersionId: string;
  variantId?: string;
  quantity: number;
}

/**
 * Hoeveel rijen een product in de investeringstabel zet. 'per_line' zet elke
 * productregel als eigen offerteregel neer (website: acht deliverables met een
 * eigen bedrag), 'collapsed' zet er één regel met het totaal neer (Positionering:
 * de uren-opbouw is intern). Niet ingevuld betekent 'collapsed'.
 */
export type ProductRowMode = 'per_line' | 'collapsed';

export interface ProductSpec {
  priceMode: PriceMode;
  variants: ProductVariant[];
  lines: ProductLine[];
  fixedPriceCents?: Record<string, number>;
  onRequestText?: string;
  cost: ProductCost;
  components?: BundleComponent[];
  showHoursDefault?: boolean;
  rowMode?: ProductRowMode;
}

export interface FormulaError {
  code?: string;
  variantId?: string;
  message: string;
}

export interface VariantComputation {
  variantId: string;
  hoursByCode: Record<string, number>;
  totalHours: number;
  lineTotalCents: Record<string, number>;
  priceCents: number | null;
  costCents: number | null;
  marginCents: number | null;
  marginPct: number | null;
  errors: FormulaError[];
}

export interface ProductComputation {
  variants: VariantComputation[];
  ratesUsed: Record<string, number>;
  computedAt: string;
  errors: FormulaError[];
}

export interface WorkbenchProduct extends ProductListItem {
  spec: ProductSpec;
  revision: string;
  computation: ProductComputation;
}

export interface ProductWorkbench {
  rateReplacements?: Record<string, string>;
  products: WorkbenchProduct[];
  rates: RateView[];
  ratesByKey: Record<string, number>;
  componentPrices: Record<string, number>;
}

export interface WorkbenchChanges {
  reason?: string;
  products: Array<{
    id: string;
    revision: string;
    spec: ProductSpec;
    billingPeriod?: BillingPeriod;
  }>;
  rates: Array<{ id: string; amountCents: number; previousCents: number | null }>;
}

export interface SpecValidationIssue {
  field: string;
  message: string;
}

/** Statustabs boven het overzicht; de groepen overlappen bewust. */
export type ProductStatusFilter = 'all' | 'review' | 'draft' | 'published' | 'archived';

export interface ProductStatusCounts {
  all: number;
  review: number;
  draft: number;
  published: number;
  archived: number;
}

/** Laatste history-regel van een product, voor de kolom "bijgewerkt". */
export interface ProductLastChange {
  at: string;
  by: string | null;
  type: string;
}

export interface HubSpotUserOption {
  ownerId: string;
  userId: string | null;
  name: string;
  email: string;
}

/** Reviewer die offertes met dit product altijd zelf moet goedkeuren. */
export type ProductReviewer = HubSpotUserOption;

/**
 * Centraal beheerde keuzegroep, bijvoorbeeld "CSM": uit de leden beoordeelt per
 * offerte één aangewezen persoon.
 */
export interface ReviewerGroup {
  id: string;
  name: string;
  members: HubSpotUserOption[];
  /** Aantal producten dat deze groep gebruikt; nodig bij verwijderen. */
  productCount: number;
}

/** Keuzegroep zoals die aan een product hangt. */
export interface ProductReviewGroupRef {
  id: string;
  name: string;
  memberCount: number;
  /** Alleen gevuld in het productdetail, niet in de lijst. */
  members?: HubSpotUserOption[];
}

export interface ProductListItem {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  labels: string[];
  variantNames?: string[];
  kind: 'service' | 'bundle';
  billingPeriod: BillingPeriod;
  unitLabel: string | null;
  enabled: boolean;
  archivedAt: string | null;
  needsReview: boolean;
  reviewNote: string | null;
  published: boolean;
  currentVersion: number | null;
  hasDraft: boolean;
  usageCount: number;
  priceRange: { minCents: number; maxCents: number } | null;
  /** Offertetekst van een gepubliceerd product op aanvraag, bv. "Vanaf € 99 per maand". */
  onRequestText?: string | null;
  lastChange: ProductLastChange | null;
  reviewers: ProductReviewer[];
  reviewGroups: ProductReviewGroupRef[];
}

export interface ProductVersionRecord {
  id: string;
  version: number;
  payload: ProductSpec;
  computed: ProductComputation;
  reason: string | null;
  publishedAt: string;
  publishedByName: string | null;
}

export interface ComponentUpdate {
  componentId: string;
  productId: string;
  productName: string;
  pinnedVersion: number;
  latestVersion: number;
  latestVersionId: string;
}

/** Herkomst uit de bronsheet, voor de nakijklijst bij een geïmporteerd product. */
export interface ImportMeta {
  sourceRows?: { hours: number[]; sellable: number[] };
  issues?: string[];
  /** Regelcode → naam uit kolom L, alleen waar die afwijkt van kolom A. */
  lineLabels?: Record<string, string>;
  variantLabels?: Record<string, string>;
}

export interface ProductDetail extends Omit<ProductListItem, 'priceRange' | 'currentVersion'> {
  hubspotPortalId: string | null;
  importMeta: ImportMeta | null;
  draftSpec: ProductSpec;
  currentVersion: ProductVersionRecord | null;
  versions: ProductVersionRecord[];
  componentUpdates: ComponentUpdate[];
  issues: SpecValidationIssue[];
  computation: ProductComputation;
  rates: RateView[];
}

export interface RateEntryView {
  id: string;
  amountCents: number;
  validFrom: string;
  reason?: string | null;
  createdByName?: string | null;
  createdAt: string;
}

export interface RateView {
  hubspotPortalId?: string | null;
  id: string;
  key: string;
  name: string;
  group?: string | null;
  sortOrder?: number | null;
  archivedAt: string | null;
  currentCents: number | null;
  upcoming: RateEntryView | null;
  entries: RateEntryView[];
}

export interface RateImpact {
  rateKey: string;
  rateName: string;
  fromCents: number | null;
  toCents: number;
  validFrom: string;
  products: Array<{
    id: string;
    name: string;
    category: string | null;
    variants: Array<{
      variantId: string;
      variantName: string;
      currentCents: number | null;
      nextCents: number | null;
    }>;
  }>;
  quotes: Array<{
    id: string;
    title: string;
    status: string;
    companyName: string | null;
    productCount: number;
  }>;
}

export interface PublishImpact {
  quotes: Array<{
    id: string;
    title: string;
    status: string;
    companyName: string | null;
    rows: number;
  }>;
  bundles: Array<{ id: string; name: string }>;
}

/** Publiceren van een selectie: wat kan mee, wat niet, en wat het raakt. */
export interface BulkPublishImpact extends PublishImpact {
  publishable: Array<{ id: string; name: string }>;
  blocked: Array<{ id: string; name: string; reason: string }>;
}

export type BulkAction =
  | 'publish'
  | 'enable'
  | 'disable'
  | 'archive'
  | 'unarchive'
  | 'add-label'
  | 'remove-label';

/** Deels slagen is bij een bulkactie de normale uitkomst, geen randgeval. */
export interface BulkOutcome {
  ok: Array<{ id: string; name: string }>;
  failed: Array<{ id: string; name: string; error: string }>;
}

export interface CatalogEvent {
  id: string;
  subjectType: 'product' | 'rate';
  subjectId: string;
  subjectName: string | null;
  type: string;
  reason: string | null;
  diff: Record<string, unknown> | null;
  versionId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  createdAt: string;
}

/** Momentopname die de offerte-editor krijgt bij het toevoegen van een product. */
export interface ProductSnapshot {
  productId: string;
  productName: string;
  productVersionId: string;
  version: number;
  billingPeriod: BillingPeriod;
  unitLabel: string | null;
  priceMode: PriceMode;
  onRequestText: string | null;
  showHoursDefault: boolean;
  rowMode: ProductRowMode;
  variantId: string;
  variantName: string;
  descriptionHtml?: string;
  priceCents: number | null;
  totalHours: number;
  pricingVariants?: import('../../../backend/src/utils/productInvestment').PricingVariant[];
  lines: Array<{
    code: string;
    description: string;
    rateKey: string;
    hours: number;
    derived: boolean;
    totalCents: number;
  }>;
}

export interface QuoteRowStatus {
  rowId: string;
  productId: string;
  productName: string;
  productEnabled: boolean;
  productArchived: boolean;
  usedVersion: number;
  latestVersion: number;
  usedVersionId: string;
  latestVersionId: string;
  outdated: boolean;
  hasOverrides: boolean;
  currentPriceCents: number;
  latestPriceCents: number | null;
}

export interface ProductUsage {
  quoteId: string;
  title: string;
  status: string;
  companyName: string | null;
  updatedAt: string;
  rows: number;
  versions: number[];
  outdated: boolean;
}
